'use client';

/**
 * Public collections gallery — /collections.
 *
 * A discovery page that lists every public collection in the system.
 * Search + sort + pagination; each tile shows the creator + first-3 prompt
 * thumbnails stacked. Clicking navigates to the existing /collection/[id]
 * detail page.
 */

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { PublicCollection } from '@/types';
import { useT } from '@/lib/i18n';

type Sort = 'newest' | 'largest';

export default function PublicCollectionsPage() {
  const t = useT();
  const [items, setItems] = useState<PublicCollection[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('newest');
  const [page, setPage] = useState(1);
  const SIZE = 24;

  // Initial + filter-change load (reset to page 1).
  useEffect(() => {
    setLoading(true);
    setPage(1);
    api.collections.public({ search: search || undefined, sort, page: 1, size: SIZE })
      .then((d) => { setItems(d.items); setTotal(d.total); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [search, sort]);

  const loadMore = async () => {
    const nextPage = page + 1;
    const d = await api.collections.public({ search: search || undefined, sort, page: nextPage, size: SIZE });
    setItems((prev) => [...prev, ...d.items]);
    setPage(nextPage);
  };

  const hasMore = items.length < total;

  return (
    <div>
      <div className="px-6 lg:px-12 py-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 max-w-5xl">
          <div>
            <span className="inline-flex items-center gap-1.5 font-label text-[11px] uppercase tracking-[0.1em] font-semibold mb-3"
              style={{ color: 'var(--accent)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>library_books</span>
              {t('collections.eyebrow')}
            </span>
            <h1
              className="font-headline text-on-surface leading-none mb-3 text-balance"
              style={{ fontSize: 'clamp(36px, 4.2vw, 56px)', letterSpacing: '-0.04em', lineHeight: 1.05 }}
            >
              {t('collections.title_before')}{' '}
              <em className="italic text-primary">{t('collections.title_em')}</em>{' '}
              {t('collections.title_after')}
            </h1>
            <p className="text-on-surface-variant text-base max-w-lg">
              {t('collections.tagline')}
            </p>
          </div>

          <div className="inline-flex bg-surface-container-low border border-outline-variant rounded-full p-[3px] gap-0.5 self-start lg:self-end">
            {(['newest', 'largest'] as Sort[]).map((s) => (
              <button
                key={s}
                onClick={() => setSort(s)}
                className={`px-3.5 py-1 rounded-full text-[12.5px] font-medium transition-colors ${
                  sort === s
                    ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {s === 'newest' ? t('collections.sort_newest') : t('collections.sort_largest')}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div style={{ marginBottom: 28, maxWidth: 480 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px',
            background: 'var(--bg-raised)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-full)',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'var(--ink-3)' }}>search</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('collections.search_placeholder')}
              style={{
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', color: 'var(--ink)',
                fontSize: 14, fontFamily: 'var(--font-sans)',
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} aria-label="Clear search"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
              </button>
            )}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{
                height: 180, background: 'var(--bg-soft)',
                border: '1px solid var(--line)', borderRadius: 'var(--radius-md)',
                opacity: 0.5,
              }} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div style={{
            padding: '60px 20px', textAlign: 'center',
            background: 'var(--bg-soft)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-md)',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 44, color: 'var(--ink-4)' }}>library_books</span>
            <div style={{
              fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 600,
              color: 'var(--ink)', marginTop: 8, marginBottom: 4, letterSpacing: '-0.02em',
            }}>
              {search ? t('collections.empty_match_title') : t('collections.empty_no_public_title')}
            </div>
            <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>
              {search ? t('collections.empty_match_body') : t('collections.empty_no_public_body')}
            </p>
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
            }}>
              {items.map((c) => <PublicCollectionCard key={c.id} collection={c} />)}
            </div>

            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 28 }}>
                <button onClick={loadMore} className="ps-btn ps-btn-secondary">
                  {t('collections.load_more')}
                  <span className="material-symbols-outlined ps-icon">expand_more</span>
                </button>
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: 16, color: 'var(--ink-4)', fontSize: 12 }}>
              Showing {items.length} of {total.toLocaleString()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PublicCollectionCard({ collection }: { collection: PublicCollection }) {
  const hue = collection.hue ?? '200';
  return (
    <Link
      href={`/collection/${collection.id}`}
      className="no-underline"
      style={{
        display: 'block',
        background: 'var(--bg-raised)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-md)',
        padding: 18,
        transition: 'transform 0.12s, border-color 0.12s, box-shadow 0.12s',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.borderColor = 'var(--ink-4)';
        e.currentTarget.style.boxShadow = '0 8px 24px -8px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.borderColor = 'var(--line)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Top: icon + stacked preview */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 14,
      }}>
        <div
          style={{
            width: 44, height: 44, borderRadius: 'var(--radius-sm)',
            display: 'grid', placeItems: 'center',
            background: `oklch(0.94 0.04 ${hue})`,
            color: `oklch(0.35 0.14 ${hue})`,
            flex: 'none',
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 22 }}>
            {collection.icon ?? 'folder'}
          </span>
        </div>
        <div style={{ position: 'relative', height: 44, width: 64, flex: 'none' }}>
          {collection.preview.slice(0, 3).map((p, i) => (
            <div
              key={p.id}
              style={{
                position: 'absolute', top: 0, right: i * 14,
                width: 36, height: 44,
                borderRadius: 6,
                overflow: 'hidden',
                background: 'var(--bg-soft)',
                border: '1.5px solid var(--bg-raised)',
                transform: `rotate(${(i - 1) * 4}deg)`,
                zIndex: 3 - i,
              }}
            >
              {p.cover_image_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={p.cover_image_url} alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  display: 'grid', placeItems: 'center',
                  fontFamily: 'var(--font-mono)', fontSize: 11,
                  color: 'var(--ink-3)',
                }}>
                  {(p.title || '?').slice(0, 1).toUpperCase()}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Title + description */}
      <h3 style={{
        fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 600,
        letterSpacing: '-0.015em', color: 'var(--ink)',
        margin: '0 0 4px',
        display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {collection.name}
      </h3>
      {collection.description && (
        <p style={{
          fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.45,
          margin: '0 0 14px',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {collection.description}
        </p>
      )}

      {/* Footer: author + count */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginTop: 10, paddingTop: 12,
        borderTop: '1px solid var(--line)',
        fontSize: 12, color: 'var(--ink-3)',
      }}>
        {collection.user.avatar_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={collection.user.avatar_url} alt=""
            style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{
            width: 18, height: 18, borderRadius: '50%',
            display: 'grid', placeItems: 'center',
            background: 'var(--bg-soft)', color: 'var(--ink-2)',
            fontSize: 9, fontWeight: 700, fontFamily: 'var(--font-serif)',
          }}>
            {collection.user.name.slice(0, 1).toUpperCase()}
          </div>
        )}
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {collection.user.name}
        </span>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          fontVariantNumeric: 'tabular-nums',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 12 }}>article</span>
          {collection.item_count}
        </span>
      </div>
    </Link>
  );
}
